import type { HomeCopy } from "./useLocaleCopy"

export function buildNav(h: HomeCopy) {
  return {
    ariaPrimary: h.nav.ariaPrimary,
    cta: h.nav.cta,
    ghost: h.nav.ghost,
    ghostHref: "https://app.mesaotc.com",
    links: [
      { label: h.nav.links.product, href: "#como-funciona" },
      { label: h.nav.links.api, href: "#api" },
      { label: h.nav.links.security, href: "#seguranca" },
      { label: h.nav.links.pricing, href: "#precos" },
      { label: h.nav.links.faq, href: "#faq" },
      { label: h.nav.links.contact, href: "#contato" },
    ],
  }
}

export function buildHero(h: HomeCopy) {
  return {
    tag: h.hero.tag,
    eyebrowStatic: "MESAOTC INFRASTRUCTURE",
    flow: {
      label: h.hero.flow.label,
      from: "USD",
      via: "USDT",
      to: "BRL",
      status: h.hero.flow.status,
    },
    eyebrow: h.hero.description,
    titleLine1: h.hero.titleLine1,
    titleLine2Prefix: h.hero.titleLine2Prefix,
    titleLine2Highlight: h.hero.titleLine2Highlight,
    description: h.hero.description,
    cta: h.hero.cta,
    ctaSecondary: h.hero.ctaSecondary,
    imageAlt: h.hero.imageAlt,
    trustLabel: h.hero.trustLabel,
    trustItems: ["Fiat", "Stablecoins", "Cripto", "Multi-rede", "Webhooks"],
  }
}

export function buildTrustMarquee(h: HomeCopy) {
  const codes: Array<keyof typeof h.trustMarquee.assetLabels> = [
    "USD",
    "BRL",
    "EUR",
    "MXN",
    "USDT",
    "USDC",
    "DAI",
    "BTC",
    "ETH",
  ]
  return {
    eyebrow: h.trustMarquee.eyebrow,
    title: h.trustMarquee.title,
    description: h.trustMarquee.description,
    assets: codes.map((code) => ({ code, label: h.trustMarquee.assetLabels[code] })),
    rails: [
      "PIX",
      "TED",
      "WIRE",
      "ACH",
      "SEPA",
      "SPEI",
      "ERC20",
      "TRC20",
      "Polygon",
      "Solana",
      "Base",
      "Lightning",
    ],
  }
}

export function buildHowItWorks(h: HomeCopy) {
  const s = h.howItWorks.steps
  return {
    eyebrow: h.howItWorks.eyebrow,
    title: h.howItWorks.title,
    description: h.howItWorks.description,
    sharedInvoiceId: "inv_01H9X4Y3K2M…",
    steps: [
      {
        number: "01",
        kicker: s["01"].kicker,
        title: s["01"].title,
        subtitle: s["01"].subtitle,
        description: s["01"].description,
        artifact: {
          kind: "code" as const,
          label: s["01"].artifactLabel,
          lines: [
            { k: s["01"].artifactKeys.charge, v: "USD 10.000,00" },
            { k: s["01"].artifactKeys.receive, v: "BRL" },
            { k: s["01"].artifactKeys.payment_options, v: "USDT · USDC · USD · BRL" },
          ],
        },
        meta: s["01"].meta,
        effect: s["01"].effect,
      },
      {
        number: "02",
        kicker: s["02"].kicker,
        title: s["02"].title,
        subtitle: s["02"].subtitle,
        description: s["02"].description,
        artifact: {
          kind: "flow" as const,
          from: { code: "USDT", rail: "TRC20", note: s["02"].fromNote },
          to: { code: "USD", rail: s["02"].toRail, note: s["02"].toNote },
        },
        meta: s["02"].meta,
        effect: s["02"].effect,
      },
      {
        number: "03",
        kicker: s["03"].kicker,
        title: s["03"].title,
        subtitle: s["03"].subtitle,
        description: s["03"].description,
        artifact: {
          kind: "event" as const,
          event: "invoice.settled",
          amount: "R$ 51.247,00",
          rail: "PIX",
          duration: "~18s",
          hmac: "HMAC-SHA256",
        },
        meta: s["03"].meta,
        effect: s["03"].effect,
        accent: true,
      },
    ],
  }
}

export function buildDifferentials(h: HomeCopy) {
  return {
    kicker: h.differentials.kicker,
    title: h.differentials.title,
    description: h.differentials.description,
    hero: {
      id: "liquidez",
      number: "01",
      title: h.differentials.hero.title,
      subtitle: h.differentials.hero.subtitle,
      description: h.differentials.hero.description,
      metric: h.differentials.hero.metric,
    },
    items: [
      {
        id: "multiativo",
        number: "02",
        title: h.differentials.items.multiativo.title,
        description: h.differentials.items.multiativo.description,
        proof: h.differentials.items.multiativo.proof,
      },
      {
        id: "multi-rede",
        number: "03",
        title: h.differentials.items.multiRede.title,
        description: h.differentials.items.multiRede.description,
        proof: h.differentials.items.multiRede.proof,
      },
      {
        id: "institucional",
        number: "04",
        title: h.differentials.items.institucional.title,
        description: h.differentials.items.institucional.description,
        proof: h.differentials.items.institucional.proof,
      },
    ],
  }
}

export function buildUseCases(h: HomeCopy) {
  const c = h.useCases.cases
  return {
    kicker: h.useCases.kicker,
    title: h.useCases.title,
    description: h.useCases.description,
    cta: h.useCases.cta,
    cases: [
      {
        id: "exportadora",
        number: "01",
        label: c.exportadora.label,
        painCta: c.exportadora.painCta,
        flow: { from: "USD · EUR", to: "BRL" },
        headline: c.exportadora.headline,
        description: c.exportadora.description,
        bullets: c.exportadora.bullets,
      },
      {
        id: "fintech",
        number: "02",
        label: c.fintech.label,
        painCta: c.fintech.painCta,
        flow: { from: "USDT · USDC · BTC", to: "BRL · USD" },
        headline: c.fintech.headline,
        description: c.fintech.description,
        bullets: c.fintech.bullets,
      },
      {
        id: "plataforma",
        number: "03",
        label: c.plataforma.label,
        painCta: c.plataforma.painCta,
        flow: { from: "Multi", to: "BRL · MXN · USD" },
        headline: c.plataforma.headline,
        description: c.plataforma.description,
        bullets: c.plataforma.bullets,
      },
      {
        id: "global",
        number: "04",
        label: c.global.label,
        painCta: c.global.painCta,
        flow: { from: "3+", to: "Ledger" },
        headline: c.global.headline,
        description: c.global.description,
        bullets: c.global.bullets,
      },
    ],
  }
}

export function buildAssetsRails(h: HomeCopy) {
  return {
    kicker: h.assetsRails.kicker,
    title: h.assetsRails.title,
    description: h.assetsRails.description,
    linkLabel: h.assetsRails.linkLabel,
    linkHref: "#contato",
    moreInBreve: h.assetsRails.moreInBreve,
    stats: [
      { value: 9, suffix: "", label: h.assetsRails.stats.moedas },
      { value: 12, suffix: "", label: h.assetsRails.stats.redes },
      { value: 3, suffix: "", label: h.assetsRails.stats.classes },
    ],
    columns: [
      {
        id: "fiat",
        label: h.assetsRails.columns.fiat.label,
        caption: h.assetsRails.columns.fiat.caption,
        items: [
          { code: "USD", label: h.assetsRails.currencyLabels.USD, rail: "WIRE · ACH" },
          { code: "BRL", label: h.assetsRails.currencyLabels.BRL, rail: "PIX · TED" },
          { code: "EUR", label: h.assetsRails.currencyLabels.EUR, rail: "SEPA" },
          { code: "MXN", label: h.assetsRails.currencyLabels.MXN, rail: "SPEI" },
        ],
      },
      {
        id: "stablecoins",
        label: h.assetsRails.columns.stablecoins.label,
        caption: h.assetsRails.columns.stablecoins.caption,
        items: [
          { code: "USDT", label: h.assetsRails.currencyLabels.USDT, rail: "ERC20 · TRC20 · Polygon" },
          { code: "USDC", label: h.assetsRails.currencyLabels.USDC, rail: "ERC20 · Solana · Base" },
          { code: "DAI", label: h.assetsRails.currencyLabels.DAI, rail: "ERC20" },
        ],
      },
      {
        id: "crypto",
        label: h.assetsRails.columns.crypto.label,
        caption: h.assetsRails.columns.crypto.caption,
        items: [
          { code: "BTC", label: h.assetsRails.currencyLabels.BTC, rail: "Mainnet · Lightning" },
          { code: "ETH", label: h.assetsRails.currencyLabels.ETH, rail: "Mainnet" },
        ],
      },
    ],
  }
}

export function buildSecurity(h: HomeCopy) {
  const p = h.security.pillars
  return {
    kicker: h.security.kicker,
    title: h.security.title,
    description: h.security.description,
    note: h.security.note,
    pillars: [
      { id: "kyc", icon: "user-check", category: p.kyc.category, title: p.kyc.title, description: p.kyc.description },
      { id: "aml", icon: "scan-search", category: p.aml.category, title: p.aml.title, description: p.aml.description },
      { id: "custody", icon: "vault", category: p.custody.category, title: p.custody.title, description: p.custody.description },
      { id: "ledger", icon: "file-stack", category: p.ledger.category, title: p.ledger.title, description: p.ledger.description },
      { id: "regulatory", icon: "scroll-text", category: p.regulatory.category, title: p.regulatory.title, description: p.regulatory.description },
      { id: "environments", icon: "shield-half", category: p.environments.category, title: p.environments.title, description: p.environments.description },
    ],
  }
}

export function buildApi(h: HomeCopy) {
  const s = h.api.spec
  return {
    kicker: h.api.kicker,
    rail: h.api.rail,
    statusLabel: h.api.statusLabel,
    statusRegion: "us-east-1 · br-south-1",
    title: h.api.title,
    description: h.api.description,
    descriptionHighlights:
      h.api.kicker.includes("desenvolvedores") || h.api.kicker.includes("developers")
        ? [] 
        : [],
    codeTitle: "POST /v1/invoices",
    codeEndpoint: "api.mesaotc.com",
    codeLatency: "47 ms",
    codeLanguage: "http",
    tabs: h.api.tabs,
    code: `POST https://api.mesaotc.com/v1/invoices
Authorization: Bearer \${API_KEY}
Content-Type: application/json
Idempotency-Key: 7f9c2a1e-3b4d-4c8e-9f1a-2e3b4d5c6f7a

{
  "receiving_currency": "BRL",
  "amount": 10000.00,
  "customer_id": "cus_01H9X2K3M4N5P6Q7R8S9T0V1W",
  "payment_options": [
    { "asset": "USDT", "network": "TRC20" },
    { "asset": "USD",  "rail": "WIRE"  }
  ]
}`,
    response: `HTTP/1.1 201 Created
x-request-id: req_01HB7K3Z8X9YF2QW3N4P5R6S7T
x-ratelimit-remaining: 998
Content-Type: application/json

{
  "id": "inv_01HB7K3Z8X9YF2QW3N4P5R6S7T",
  "status": "awaiting_payment",
  "receiving_currency": "BRL",
  "amount": 10000.00,
  "payment_url": "https://pay.mesaotc.com/inv_01HB7K...",
  "expires_at": "2026-04-24T18:00:00Z",
  "created_at": "2026-04-23T18:00:00Z"
}`,
    stats: [
      { id: "uptime", value: 99.95, suffix: "%", label: h.api.stats.uptime, decimals: 2 },
      { id: "latency", value: 47, suffix: " ms", label: h.api.stats.latency },
      { id: "regions", value: 3, suffix: "", label: h.api.stats.regions },
      { id: "sdks", value: 4, suffix: "", label: h.api.stats.sdks },
    ],
    spec: [
      { id: "protocol", label: s.protocol.label, value: s.protocol.value, note: s.protocol.note },
      { id: "auth", label: s.auth.label, value: s.auth.value, note: s.auth.note },
      { id: "limits", label: s.limits.label, value: s.limits.value, note: s.limits.note },
      { id: "versioning", label: s.versioning.label, value: s.versioning.value, note: s.versioning.note },
      { id: "sdks", label: s.sdks.label, value: s.sdks.value, note: s.sdks.note },
      { id: "sandbox", label: s.sandbox.label, value: s.sandbox.value, note: s.sandbox.note },
    ],
    ctaPrimary: { label: h.api.ctaPrimary, href: "https://docs.mesaotc.com" },
    ctaSecondary: { label: h.api.ctaSecondary, href: "#contato" },
    copyLabel: h.api.copyLabel,
    copiedLabel: h.api.copiedLabel,
    signature: h.api.signature,
  }
}

export function buildPricing(h: HomeCopy) {
  const a = h.pricing.attributes
  const p = h.pricing.process
  return {
    kicker: h.pricing.kicker,
    rail: h.pricing.rail,
    tag: h.pricing.tag,
    title: h.pricing.title,
    titleHighlights: [],
    description: h.pricing.description,
    descriptionHighlights: [],
    formula: {
      label: h.pricing.formulaLabel,
      equation: "fee = spread + taxa_liquidacao(volume, ativo)",
      legend: [
        { token: "spread", note: h.pricing.legend.spread },
        { token: "volume", note: h.pricing.legend.volume },
        { token: "ativo", note: h.pricing.legend.ativo },
      ],
    },
    attributes: [
      { label: a.setupFee.label, value: a.setupFee.value, note: a.setupFee.note },
      { label: a.spread.label, value: a.spread.value, note: a.spread.note },
      { label: a.taxa.label, value: a.taxa.value, note: a.taxa.note },
      { label: a.contrato.label, value: a.contrato.value, note: a.contrato.note },
    ],
    process: [
      { id: "01", label: p["01"].label, detail: p["01"].detail },
      { id: "02", label: p["02"].label, detail: p["02"].detail },
      { id: "03", label: p["03"].label, detail: p["03"].detail },
    ],
    primaryCta: { label: h.pricing.primaryCta, href: "#contato" },
    ctaHelper: h.pricing.ctaHelper,
    note: h.pricing.note,
    signature: h.pricing.signature,
  }
}

export function buildFaq(h: HomeCopy) {
  const i = h.faq.items
  return {
    kicker: h.faq.kicker,
    title: h.faq.title,
    description: h.faq.description,
    items: [
      { id: "what", question: i.what.question, answer: i.what.answer },
      { id: "liquidation", question: i.liquidation.question, answer: i.liquidation.answer },
      { id: "assets", question: i.assets.question, answer: i.assets.answer },
      { id: "compliance", question: i.compliance.question, answer: i.compliance.answer },
      { id: "integration", question: i.integration.question, answer: i.integration.answer },
      { id: "costs", question: i.costs.question, answer: i.costs.answer },
      { id: "sla", question: i.sla.question, answer: i.sla.answer },
      { id: "api-access", question: i["api-access"].question, answer: i["api-access"].answer },
    ],
  }
}

export function buildContact(h: HomeCopy) {
  const f = h.contact.form
  const v = f.volumeOptions
  const r = h.contact.reassuranceSpec
  return {
    kicker: h.contact.kicker,
    rail: h.contact.rail,
    title: h.contact.title,
    description: h.contact.description,
    reassurance: h.contact.reassurance,
    reassuranceSpec: [
      { id: "response", label: r.response.label, value: r.response.value },
      { id: "assessment", label: r.assessment.label, value: r.assessment.value },
      { id: "privacy", label: r.privacy.label, value: r.privacy.value },
    ],
    successTitle: h.contact.successTitle,
    successMessage: h.contact.successMessage,
    successHint: h.contact.successHint,
    successLinkLabel: h.contact.successLinkLabel,
    submitCta: h.contact.submitCta,
    submitMicrocopy: h.contact.submitMicrocopy,
    signature: h.contact.signature,
    altChannels: {
      label: h.contact.altChannels.label,
      directLabel: h.contact.altChannels.directLabel,
      agendaLabel: h.contact.altChannels.agendaLabel,
      agendaValue: h.contact.altChannels.agendaValue,
      email: "contato@mesaotc.com",
      emailLink: "mailto:contato@mesaotc.com",
      agendaHref: `https://${h.contact.altChannels.agendaValue}`,
    },
    form: {
      sectionLabel: f.sectionLabel,
      nameLabel: f.nameLabel,
      namePlaceholder: f.namePlaceholder,
      nameHint: f.nameHint,
      companyLabel: f.companyLabel,
      companyPlaceholder: f.companyPlaceholder,
      companyHint: f.companyHint,
      emailLabel: f.emailLabel,
      emailPlaceholder: f.emailPlaceholder,
      emailHint: f.emailHint,
      volumeLabel: f.volumeLabel,
      volumePlaceholder: f.volumePlaceholder,
      volumeHint: f.volumeHint,
      volumeOptions: [
        { value: "ate-30k", label: v["ate-30k"] },
        { value: "30k-100k", label: v["30k-100k"] },
        { value: "100k-500k", label: v["100k-500k"] },
        { value: "500k-1m", label: v["500k-1m"] },
        { value: "1m-5m", label: v["1m-5m"] },
        { value: "5m+", label: v["5m+"] },
        { value: "avaliando", label: v.avaliando },
      ],
      messageLabel: f.messageLabel,
      messagePlaceholder: f.messagePlaceholder,
      messageHint: f.messageHint,
      validation: f.validation,
    },
  }
}

export function buildFinalCta(h: HomeCopy) {
  return {
    title: h.finalCta.title,
    description: h.finalCta.description,
    primaryCta: { label: h.finalCta.primaryCta, href: "#contato" },
    secondaryCta: {
      label: h.finalCta.secondaryCta,
      href: "https://docs.mesaotc.com",
      external: true,
    },
  }
}

export function buildFooter(h: HomeCopy) {
  const c = h.footer.columns
  return {
    brand: {
      name: "MesaOTC",
      tagline: h.footer.tagline,
    },
    columns: [
      {
        label: c.product.label,
        links: [
          { label: c.product.links.howItWorks, href: "#como-funciona" },
          { label: c.product.links.useCases, href: "#casos-de-uso" },
          { label: c.product.links.assets, href: "#ativos" },
          { label: c.product.links.security, href: "#seguranca" },
          { label: c.product.links.pricing, href: "#precos" },
        ],
      },
      {
        label: c.developer.label,
        links: [
          { label: c.developer.links.docs, href: "https://docs.mesaotc.com", external: true },
          { label: c.developer.links.apiReference, href: "https://docs.mesaotc.com/api", external: true },
          { label: c.developer.links.status, href: "https://status.mesaotc.com", external: true },
          { label: c.developer.links.changelog, href: "https://docs.mesaotc.com/changelog", external: true },
        ],
      },
      {
        label: c.company.label,
        links: [
          { label: c.company.links.useCases, href: "#casos-de-uso" },
          { label: c.company.links.contact, href: "#contato" },
          { label: c.company.links.faq, href: "#faq" },
        ],
      },
      {
        label: c.legal.label,
        links: [
          { label: c.legal.links.privacy, href: "/privacidade" },
          { label: c.legal.links.terms, href: "/termos" },
          { label: c.legal.links.compliance, href: "/compliance" },
        ],
      },
    ],
    socials: [
      { label: "LinkedIn", href: "https://linkedin.com/company/mesaotc" },
      { label: "X", href: "https://x.com/mesaotc" },
      { label: "GitHub", href: "https://github.com/mesaotc" },
    ],
    meta: {
      address: h.footer.meta.address,
      legalName: h.footer.meta.legalName,
      year: 2026,
      statusLabel: h.footer.meta.statusLabel,
      statusHref: "https://status.mesaotc.com",
      region: h.footer.meta.region,
    },
  }
}
